import { useQueryClient } from '@tanstack/react-query';
import { queryUtils, useDeleteCollectionMutation, useRenameCollectionMutation } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import * as React from 'react';

interface UseCollectionMenuActionsOptions {
  /**
   * Portfolio ID for query invalidation
   */
  portfolioId?: string;
  /**
   * Optional callback to navigate after deletion
   * If provided, will be called after successful delete
   */
  onNavigateAfterDelete?: () => void | Promise<void>;
}

interface UseCollectionMenuActionsReturn {
  /**
   * Handler for renaming a collection
   * @param collectionId - The ID of the collection to rename
   * @param newName - The new name for the collection
   */
  handleRename: (collectionId: string, newName: string) => Promise<void>;
  /**
   * Handler for deleting a collection
   * @param collectionId - The ID of the collection to delete
   */
  handleDelete: (collectionId: string) => Promise<void>;
}

/**
 * Hook that provides handlers for collection menu actions (rename, delete)
 * Encapsulates toast notifications, query invalidation, and navigation logic
 *
 * @param options - Configuration options
 * @returns Handlers for collection menu actions
 *
 * @example
 * ```tsx
 * const { handleRename, handleDelete } = useCollectionMenuActions({
 *   portfolioId,
 *   onNavigateAfterDelete: () => router.navigate({ to: '/library/collections' }),
 * });
 *
 * <CollectionMenuDropdown
 *   collectionId={id}
 *   onSuccessfulRename={handleRename}
 *   onSuccessfulDelete={handleDelete}
 * />
 * ```
 */
export function useCollectionMenuActions(options: UseCollectionMenuActionsOptions): UseCollectionMenuActionsReturn {
  const { portfolioId, onNavigateAfterDelete } = options;
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const renameMutation = useRenameCollectionMutation();
  const deleteMutation = useDeleteCollectionMutation();

  const handleRename = React.useCallback(
    async (collectionId: string, newName: string) => {
      if (!portfolioId) {
        addToast({
          title: 'Error',
          description: 'Portfolio ID is required to rename a collection',
          level: AlertLevel.Error,
        });
        return;
      }

      try {
        await renameMutation.mutateAsync({
          portfolio_id: portfolioId,
          collection_id: collectionId,
          name: newName,
        });

        addToast({
          title: 'Collection renamed',
          description: `Collection renamed to "${newName}"`,
          level: AlertLevel.Success,
        });

        // Invalidate all collection lists and the specific collection detail
        void queryUtils.invalidateAllCollectionLists(queryClient, portfolioId);
        void queryUtils.invalidateCollectionDetail(queryClient, portfolioId, collectionId);
      } catch (error) {
        addToast({
          title: 'Error',
          description: 'Failed to rename collection',
          level: AlertLevel.Error,
        });
      }
    },
    [portfolioId, queryClient, addToast, renameMutation],
  );

  const handleDelete = React.useCallback(
    async (collectionId: string) => {
      if (!portfolioId) {
        addToast({
          title: 'Error',
          description: 'Portfolio ID is required to delete a collection',
          level: AlertLevel.Error,
        });
        return;
      }

      try {
        await deleteMutation.mutateAsync({
          portfolio_id: portfolioId,
          collection_id: collectionId,
        });

        addToast({
          title: 'Collection deleted',
          description: 'Collection has been deleted',
          level: AlertLevel.Success,
        });

        // Invalidate all collection lists (fire-and-forget)
        void queryUtils.invalidateAllCollectionLists(queryClient, portfolioId);

        // Navigate if callback provided (e.g., from details page)
        if (onNavigateAfterDelete) {
          await onNavigateAfterDelete();
        }
      } catch (error) {
        addToast({
          title: 'Error',
          description: 'Failed to delete collection',
          level: AlertLevel.Error,
        });
      }
    },
    [portfolioId, queryClient, addToast, deleteMutation, onNavigateAfterDelete],
  );

  return {
    handleRename,
    handleDelete,
  };
}
